import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { track } from '../../utils/tracking';

export default function CommercialRealEstatePage() {
  useEffect(() => {
    track('page_view', { page: 'commercial_real_estate_funding' });
    
    document.title = 'Commercial Real Estate Loans | Business Property Financing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about commercial real estate loans for purchasing, refinancing, or developing business properties. Comprehensive guide to CRE financing options, requirements, and terms.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'commercial real estate loans, CRE financing, business property loans, commercial mortgage, investment property financing');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.href);
    }
  }, []);

  const propertyTypes = [
    {
      icon: 'ri-building-4-line',
      title: 'Office Buildings',
      description: 'Single or multi-tenant office spaces for professional services, corporate headquarters, or medical offices.',
      range: '$500K - $50M+'
    },
    {
      icon: 'ri-store-3-line',
      title: 'Retail Properties',
      description: 'Shopping centers, strip malls, standalone retail buildings, and mixed-use retail spaces.',
      range: '$300K - $20M+'
    },
    {
      icon: 'ri-home-4-line',
      title: 'Multi-Family',
      description: 'Apartment buildings, condominiums, and residential complexes with 5 or more units.',
      range: '$400K - $30M+'
    },
    {
      icon: 'ri-truck-line',
      title: 'Industrial/Warehouse',
      description: 'Manufacturing facilities, distribution centers, warehouses, and flex spaces.',
      range: '$500K - $40M+'
    },
    {
      icon: 'ri-hotel-line',
      title: 'Hospitality',
      description: 'Hotels, motels, resorts, and other lodging facilities for business or leisure travelers.',
      range: '$1M - $100M+'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Special Purpose',
      description: 'Churches, schools, gas stations, car washes, and other specialized commercial properties.',
      range: '$250K - $10M+'
    }
  ];

  const loanTypes = [
    {
      title: 'Purchase Loans',
      description: 'Finance the acquisition of commercial property for owner-occupied business use or investment purposes.',
      features: ['65-80% LTV', '20-35% down payment', '5-25 year terms', 'Fixed or variable rates']
    },
    {
      title: 'Refinance Loans',
      description: 'Refinance existing commercial mortgages to lower rates, access equity, or improve loan terms.',
      features: ['Up to 75% LTV', 'Cash-out options', 'Rate and term refinance', 'Debt consolidation']
    },
    {
      title: 'Construction Loans',
      description: 'Short-term financing for ground-up construction or major renovation of commercial properties.',
      features: ['Interest-only payments', '12-36 month terms', 'Draw schedules', 'Convert to permanent']
    },
    {
      title: 'Bridge Loans',
      description: 'Short-term financing to bridge gaps between property purchase and permanent financing or sale.',
      features: ['Fast approval', '6-24 month terms', 'Higher rates', 'Flexible exit strategies']
    }
  ];

  const qualificationTable = [
    {
      factor: 'Credit Score',
      typical: '680+',
      ideal: '720+',
      notes: 'Personal and business credit reviewed'
    },
    {
      factor: 'Down Payment',
      typical: '20-35%',
      ideal: '25-30%',
      notes: 'Varies by property type and loan program'
    },
    {
      factor: 'DSCR',
      typical: '1.25x',
      ideal: '1.35x+',
      notes: 'Debt Service Coverage Ratio - property income vs. debt'
    },
    {
      factor: 'Time in Business',
      typical: '2+ years',
      ideal: '5+ years',
      notes: 'Longer history improves terms'
    },
    {
      factor: 'Net Worth',
      typical: 'Equal to loan',
      ideal: '1.5x loan',
      notes: 'Personal net worth requirement'
    },
    {
      factor: 'Liquidity',
      typical: '6-12 months',
      ideal: '12+ months',
      notes: 'Reserves for property expenses'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Pre-Qualification',
      timeline: '1-2 weeks',
      description: 'Submit initial application with business and property information. Lender reviews financials and provides preliminary terms.',
      tips: ['Prepare financial statements', 'Identify target property', 'Understand your budget']
    },
    {
      step: 2,
      title: 'Property Identification',
      timeline: '2-8 weeks',
      description: 'Find and negotiate purchase of commercial property. Execute purchase agreement or letter of intent.',
      tips: ['Work with commercial broker', 'Conduct initial due diligence', 'Negotiate favorable terms']
    },
    {
      step: 3,
      title: 'Formal Application',
      timeline: '1-2 weeks',
      description: 'Submit complete loan application with all required financial documents and property information.',
      tips: ['Gather all documentation', 'Provide detailed property info', 'Be responsive to requests']
    },
    {
      step: 4,
      title: 'Property Due Diligence',
      timeline: '3-6 weeks',
      description: 'Order and complete property appraisal, environmental report (Phase I), title search, and survey.',
      tips: ['Choose qualified vendors', 'Address issues promptly', 'Review reports carefully']
    },
    {
      step: 5,
      title: 'Underwriting',
      timeline: '4-8 weeks',
      description: 'Lender conducts comprehensive review of financials, property reports, and loan structure.',
      tips: ['Respond quickly to requests', 'Provide clarifications', 'Stay in communication']
    },
    {
      step: 6,
      title: 'Closing',
      timeline: '1-2 weeks',
      description: 'Final loan approval, document signing, and funding. Property ownership transfers to buyer.',
      tips: ['Review closing documents', 'Arrange insurance', 'Prepare for closing costs']
    }
  ];

  const successStories = [
    {
      business: 'Medical Practice Expansion',
      property: 'Medical Office Building',
      amount: '$2,500,000',
      downPayment: '25% ($625,000)',
      outcome: 'Purchased 12,000 sq ft medical office building, consolidated three locations into one, reduced rent by $8,000/month, building appreciated 18% in 3 years.'
    },
    {
      business: 'Multi-Family Investment',
      property: '24-Unit Apartment Complex',
      amount: '$3,200,000',
      downPayment: '30% ($960,000)',
      outcome: 'Acquired apartment building with 85% occupancy, increased rents 12%, improved to 95% occupancy, generates $15,000/month positive cash flow.'
    },
    {
      business: 'Manufacturing Facility',
      property: 'Industrial Warehouse',
      amount: '$4,800,000',
      downPayment: '25% ($1,200,000)',
      outcome: 'Purchased 45,000 sq ft manufacturing facility, eliminated $25,000/month rent, expanded production capacity by 40%, property value increased 22%.'
    },
    {
      business: 'Retail Plaza Investment',
      property: 'Strip Mall',
      amount: '$1,800,000',
      downPayment: '30% ($540,000)',
      outcome: 'Acquired 8-unit retail plaza with national tenants, 100% occupied with long-term leases, generates $12,000/month cash flow, refinanced after 3 years to access equity.'
    }
  ];

  const faqs = [
    {
      question: 'What is a commercial real estate loan?',
      answer: 'A commercial real estate (CRE) loan is financing used to purchase, refinance, or develop income-producing or business-use properties. Unlike residential mortgages, CRE loans are secured by commercial properties and evaluated based on property income, business financials, and borrower creditworthiness. Terms typically range from 5-25 years with amortization periods of 20-25 years.'
    },
    {
      question: 'How much down payment is required for commercial real estate?',
      answer: 'Commercial real estate loans typically require 20-35% down payment depending on property type, borrower strength, and loan program. Owner-occupied properties may qualify for lower down payments (10-15% with SBA 504 loans), while investment properties typically require 25-35% down. Stronger borrowers with excellent credit and financials may negotiate lower down payments.'
    },
    {
      question: 'What is Debt Service Coverage Ratio (DSCR) and why does it matter?',
      answer: 'DSCR measures whether property income covers debt payments. It is calculated by dividing Net Operating Income (NOI) by annual debt service. Lenders typically require DSCR of 1.25-1.5x, meaning property income must exceed debt payments by 25-50%. For example, if annual debt payments are $100,000, NOI should be $125,000-$150,000. Higher DSCR indicates lower risk and may qualify for better terms.'
    },
    {
      question: 'How long does the commercial real estate loan process take?',
      answer: 'Commercial real estate loans typically take 60-90 days from application to closing. The timeline includes pre-qualification (1-2 weeks), property identification (2-8 weeks if not already identified), formal application (1-2 weeks), property due diligence including appraisal and environmental reports (3-6 weeks), underwriting (4-8 weeks), and closing (1-2 weeks). Complex properties, environmental issues, or title problems may extend the timeline.'
    },
    {
      question: 'What are typical commercial real estate loan terms and rates?',
      answer: 'Commercial real estate loans typically have terms of 5-25 years with amortization periods of 20-25 years. Many loans have balloon payments due after 5-10 years, requiring refinancing. Interest rates vary based on property type, borrower strength, and market conditions. Rates may be fixed for the entire term or adjust after an initial fixed period (e.g., 5-year fixed, then adjustable). Current rates typically range from 6-10% depending on these factors.'
    },
    {
      question: 'What is the difference between owner-occupied and investment property loans?',
      answer: 'Owner-occupied properties are where your business operates (at least 51% of space), while investment properties are purchased solely for rental income. Owner-occupied loans typically offer lower down payments (15-25%), better rates, and more favorable terms because the business generates income to support payments. Investment property loans require higher down payments (25-35%) and stronger DSCR because they rely solely on rental income. SBA loans are only available for owner-occupied properties.'
    },
    {
      question: 'What documents are required for commercial real estate loans?',
      answer: 'Required documents typically include: 2-3 years of business tax returns, 2-3 years of personal tax returns, business financial statements (profit & loss, balance sheet), personal financial statement, 6-12 months of business bank statements, property appraisal, environmental report (Phase I), title search and survey, lease agreements (for investment properties), purchase agreement or letter of intent, business plan (for development projects), and proof of insurance. Lenders may request additional documentation during underwriting.'
    },
    {
      question: 'Can I get a commercial real estate loan for a startup business?',
      answer: 'Commercial real estate loans for startups are challenging but possible with strong personal financials, significant down payment (30-40%), and solid business plan. SBA 504 loans are more startup-friendly, requiring 10-15% down for owner-occupied properties. Lenders focus heavily on personal credit (720+), net worth, liquidity, and industry experience. Having an existing business with revenue history significantly improves approval odds and terms.'
    },
    {
      question: 'What are the tax benefits of commercial real estate ownership?',
      answer: 'Commercial real estate offers several tax benefits: mortgage interest is tax-deductible, property depreciation provides annual deductions (typically 39 years for commercial buildings), operating expenses (maintenance, insurance, property taxes) are deductible, and 1031 exchanges allow deferring capital gains taxes when selling and purchasing replacement property. Owner-occupied properties may qualify for additional deductions. Consult with a tax professional to maximize benefits for your specific situation.'
    },
    {
      question: 'What happens if I default on a commercial real estate loan?',
      answer: 'Defaulting on a commercial real estate loan can result in foreclosure, where the lender takes ownership of the property to recover the debt. This damages personal and business credit significantly (7-10 years), may result in deficiency judgments if property sale does not cover the debt, and can lead to personal liability if you provided a personal guarantee. If facing financial difficulties, contact your lender immediately to discuss workout options such as loan modification, forbearance, or deed-in-lieu of foreclosure to minimize damage.'
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      
      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200 py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm flex-wrap">
              <Link to="/" className="text-gray-600 hover:text-[#C22F3B] transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <Link to="/business-funding" className="text-gray-600 hover:text-[#C22F3B] transition-colors">
                Business Funding
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <span className="text-gray-900 font-semibold">Commercial Real Estate Loans</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-50 via-blue-50 to-white py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-indigo-600 to-blue-600 rounded-lg">
                  <i className="ri-building-line text-white text-2xl" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Commercial Real Estate Loans</h1>
              </div>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Finance the purchase, refinance, or development of commercial properties with long-term loans designed for business real estate investments. Build equity, control your business location, and benefit from property appreciation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/prequalification"
                  onClick={() => track('cta_clicked', { cta: 'hero_prequalification', funding_type: 'commercial_real_estate' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-lg font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all whitespace-nowrap"
                >
                  Check Your Options
                  <i className="ri-arrow-right-line ml-2" />
                </Link>
                <Link
                  to="/business-funding"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-indigo-600 text-indigo-600 text-lg font-semibold rounded-lg hover:bg-indigo-50 transition-all whitespace-nowrap"
                >
                  View All Funding Types
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key Stats */}
        <section className="py-12 px-4 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">$100K - $10M+</div>
                <div className="text-gray-600">Typical Loan Range</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">5-25 Years</div>
                <div className="text-gray-600">Loan Terms</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">20-35%</div>
                <div className="text-gray-600">Down Payment</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">60-90 Days</div>
                <div className="text-gray-600">Typical Timeline</div>
              </div>
            </div>
          </div>
        </section>

        {/* What is CRE Financing */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  What is Commercial Real Estate Financing?
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Commercial real estate (CRE) loans are used to purchase, refinance, or develop income-producing or business-use properties. Unlike residential mortgages, CRE loans are secured by commercial properties and evaluated based on property income, business financials, and borrower creditworthiness.
                  </p>
                  <p>
                    These loans typically have terms of 5-25 years with amortization periods of 20-25 years. Many loans have balloon payments due after 5-10 years, requiring refinancing at that time. Interest rates may be fixed for the entire term or adjust after an initial fixed period.
                  </p>
                  <p>
                    Commercial real estate financing allows businesses to build equity, control their operating location, benefit from property appreciation, and gain tax advantages through depreciation and interest deductions.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose CRE Financing?</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-indigo-600 text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Build Equity</div>
                      <div className="text-gray-600 text-sm">Monthly payments build ownership rather than paying rent to landlords</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-indigo-600 text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Property Appreciation</div>
                      <div className="text-gray-600 text-sm">Benefit from property value increases over time</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-indigo-600 text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Tax Benefits</div>
                      <div className="text-gray-600 text-sm">Deduct interest, depreciation, and operating expenses</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-indigo-600 text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Control & Stability</div>
                      <div className="text-gray-600 text-sm">No landlord restrictions or rent increases</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-indigo-600 text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Additional Income</div>
                      <div className="text-gray-600 text-sm">Lease excess space to generate rental income</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-indigo-600 text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Leverage Equity</div>
                      <div className="text-gray-600 text-sm">Refinance to access equity for business growth</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Property Types */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Property Types We Finance
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Commercial real estate loans cover a wide range of property types for business use or investment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {propertyTypes.map((property, index) => (
                <div key={index} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-8 border border-indigo-100">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-indigo-600 to-blue-600 rounded-lg mb-6">
                    <i className={`${property.icon} text-white text-2xl`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{property.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm">{property.description}</p>
                  <div className="text-sm font-semibold text-indigo-600">
                    Typical Range: {property.range}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Loan Types */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Types of Commercial Real Estate Loans
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Different loan structures for different commercial real estate needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {loanTypes.map((loanType, index) => (
                <div key={index} className="bg-white rounded-xl p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{loanType.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{loanType.description}</p>
                  <div className="space-y-2">
                    {loanType.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-center gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-indigo-600" />
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Qualification Requirements */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Qualification Requirements
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding what lenders look for in commercial real estate loan applications.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                        <th className="px-6 py-4 text-left font-semibold">Factor</th>
                        <th className="px-6 py-4 text-left font-semibold">Typical Minimum</th>
                        <th className="px-6 py-4 text-left font-semibold">Ideal</th>
                        <th className="px-6 py-4 text-left font-semibold">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qualificationTable.map((row, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="px-6 py-4 font-semibold text-gray-900">{row.factor}</td>
                          <td className="px-6 py-4 text-gray-700">{row.typical}</td>
                          <td className="px-6 py-4 text-indigo-600 font-semibold">{row.ideal}</td>
                          <td className="px-6 py-4 text-gray-600 text-sm">{row.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                The Application Process
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Step-by-step guide to securing commercial real estate financing.
              </p>
            </div>

            <div className="max-w-5xl mx-auto space-y-6">
              {processSteps.map((step, index) => (
                <div key={index} className="bg-white rounded-xl p-8 border border-gray-200">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-indigo-600 to-blue-600 text-white text-xl font-bold rounded-lg flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                        <span className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full whitespace-nowrap">
                          {step.timeline}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4 leading-relaxed">{step.description}</p>
                      <div className="bg-indigo-50 rounded-lg p-4">
                        <div className="font-semibold text-gray-900 mb-2 text-sm">Key Tips:</div>
                        <div className="space-y-1">
                          {step.tips.map((tip, tIndex) => (
                            <div key={tIndex} className="flex items-start gap-2">
                              <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 text-sm">{tip}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Real Success Stories
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                See how businesses have used commercial real estate financing to grow and build wealth.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {successStories.map((story, index) => (
                <div key={index} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-8 border border-indigo-100">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-indigo-600 to-blue-600 rounded-lg flex-shrink-0">
                      <i className="ri-building-line text-white text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{story.business}</h3>
                      <div className="text-indigo-600 font-semibold">{story.property}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-indigo-200">
                    <div>
                      <div className="text-sm text-gray-600">Loan Amount</div>
                      <div className="font-bold text-gray-900">{story.amount}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Down Payment</div>
                      <div className="font-bold text-gray-900">{story.downPayment}</div>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{story.outcome}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Pros &amp; Cons
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the advantages and considerations of commercial real estate financing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl p-8 border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                    <i className="ri-thumb-up-line text-green-600 text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Advantages</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Build Equity &amp; Wealth</div>
                      <div className="text-gray-600 text-sm">Payments build ownership and property appreciation increases net worth</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Tax Benefits</div>
                      <div className="text-gray-600 text-sm">Deduct interest, depreciation, and operating expenses</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Control &amp; Stability</div>
                      <div className="text-gray-600 text-sm">No landlord restrictions, rent increases, or lease expirations</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Additional Income Potential</div>
                      <div className="text-gray-600 text-sm">Lease excess space to generate rental income</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Leverage for Growth</div>
                      <div className="text-gray-600 text-sm">Refinance to access equity for business expansion</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Long-Term Investment</div>
                      <div className="text-gray-600 text-sm">Property typically appreciates over time, building long-term wealth</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg">
                    <i className="ri-alert-line text-amber-600 text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Considerations</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Large Down Payment</div>
                      <div className="text-gray-600 text-sm">Requires 20-35% down payment, tying up significant capital</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Long-Term Commitment</div>
                      <div className="text-gray-600 text-sm">5-25 year loans with balloon payments requiring refinancing</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Property Maintenance</div>
                      <div className="text-gray-600 text-sm">Responsible for all repairs, maintenance, and property expenses</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Market Risk</div>
                      <div className="text-gray-600 text-sm">Property values can decline during economic downturns</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Less Flexibility</div>
                      <div className="text-gray-600 text-sm">Harder to relocate or downsize compared to leasing</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-gray-900">Complex Process</div>
                      <div className="text-gray-600 text-sm">60-90 day process with extensive documentation and due diligence</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">
                Common questions about commercial real estate financing
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden group"
                >
                  <summary className="px-8 py-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h3>
                    <i className="ri-add-line text-2xl text-indigo-600 group-open:rotate-45 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-8 pb-6 text-gray-600 leading-relaxed">{faq.answer}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Explore Commercial Real Estate Financing?</h2>
            <p className="text-xl mb-8 text-indigo-50">
              Complete our pre-qualification form to see what commercial real estate loan options may be available for your business.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/prequalification"
                onClick={() => track('cta_clicked', { cta: 'bottom_prequalification', funding_type: 'commercial_real_estate' })}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-indigo-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Start Pre-Qualification
                <i className="ri-arrow-right-line ml-2" />
              </Link>
              <Link
                to="/business-funding"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                View All Funding Types
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
